import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme/ThemeContext';
import { 
  Users,  
  Activity, 
  Shield, 
  Download, 
  Search, 
  Filter, 
  MoreHorizontal,
  AlertTriangle,
  CheckCircle,
  Eye,
  UserCheck,
  Database,
  Server,
  Cpu,
  HardDrive,
  Wifi,
  Clock,
  TrendingUp,
  FileText,
  RefreshCw,
  Moon,
  Sun,
  UserX,
  Trash2,
  Crown,
  Upload,
  Settings,
  X,
  Newspaper,
  GraduationCap,
  Map,
  ShieldQuestion,
  StickyNote,
  Mic,
  Briefcase
} from 'lucide-react';

import apiClient from '../../redux/authentication/utils/core/axios';

import {
  fetchAdminHealthThunk,
  fetchAdminSystemStatsThunk,
  fetchAdminUsersListThunk,
  fetchAdminAuditLogsThunk,
  bulkAdminUserActionThunk,
  eraseAdminUserThunk,
  exportAdminDataThunk,
  fetchAdminUserThunk,
} from '../../redux/admin/adminActionThunks';
import { clearUserDetails } from '../../redux/admin/adminSlice';

import CSVImportSystem from './importFiles/importCSV';

import AdminModerationComponent from './moderation/AdminModerationComponent';
import AdminCredentialQuizComponent from './credentialQuiz/AdminCredentialQuizComponent';
import AdminEvidenceModerationComponent from './moderation/AdminEvidenceModerationComponent';
import AdminNewsModerationComponent from './moderation/AdminNewsModerationComponent';
import AdminPlatformStatusComponent from './platformStatus/AdminPlatformStatusComponent';
import AdminStatusMapComponent from './statusMap/AdminStatusMapComponent';
import AdminReviewRequestsComponent from './reviewRequests/AdminReviewRequestsComponent';
import AdminMeetingNotesComponent from './meetingNotes/AdminMeetingNotesComponent';
import AdminCompanyOpsComponent from './companyOps/AdminCompanyOpsComponent';
import AdminTeamMeetingsComponent from './teamMeetings/AdminTeamMeetingsComponent';
import TasksAPI from '../../redux/admin/tasksApi';

/**
 * Cross-link from the Users tab's detail modal into Company Ops: what
 * Task Delegation work is this person currently assigned. Reads via a
 * direct API call rather than dispatching the shared `tasks` slice's
 * fetchTasksThunk -- that thunk overwrites the whole `state.tasks.items`
 * list, which would clobber whatever the Company Ops tab itself is showing
 * if a manager has both open across tabs. This is read-only, page-local
 * supplementary info, not something the rest of the app needs to react to.
 */
const UserAssignedTasksSection = ({ userId, themeClasses, t }) => {
  const [tasks, setTasks] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTasks(null);
    setLoadError(null);
    TasksAPI.fetchTasks({ assignee_id: userId })
      .then((data) => { if (!cancelled) setTasks(data); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div className={`mt-4 pt-4 border-t ${themeClasses.border}`}>
      <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${themeClasses.textMuted}`}>
        {t('admin.dashboard.users.detailsModal.assignedTasks')}
      </h4>
      {tasks === null && !loadError && (
        <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.dashboard.users.detailsModal.loading')}</p>
      )}
      {loadError && <p className="text-xs text-red-500">{t('admin.dashboard.users.detailsModal.assignedTasksError')}</p>}
      {tasks && tasks.length === 0 && (
        <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.dashboard.users.detailsModal.noAssignedTasks')}</p>
      )}
      {tasks && tasks.length > 0 && (
        <ul className="space-y-1.5">
          {tasks.slice(0, 5).map((task) => (
            <li key={task.id} className={`text-xs flex items-center justify-between gap-2 ${themeClasses.text}`}>
              <span className="truncate">{task.title}</span>
              <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${themeClasses.textMuted}`}>
                {task.status.replace('_', ' ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Deep-link support for notifications (?tab=companyOps&sub=tasks&taskId=123)
  // -- this is the only place in the whole admin dashboard the URL is read;
  // in-page tab switches otherwise stay local useState, no history entries.
  const [searchParams] = useSearchParams();
  const deepLinkTab = searchParams.get('tab');
  const deepLinkSub = searchParams.get('sub');
  const deepLinkTaskId = searchParams.get('taskId');
  const [activeTab, setActiveTab] = useState(deepLinkTab || 'overview');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Was its own independent `useState(true)`, disconnected from the
  // site-wide theme (see ThemeContext.jsx/leftSideBar.jsx's toggle) --
  // always booted dark regardless of the user's saved global preference,
  // and could disagree with the real `dark` class Tailwind's `dark:`
  // variant reads elsewhere on the page. `darkMode` below is now derived
  // from the same global context every other page uses; every existing
  // `darkMode ? x : y` conditional in this file is unchanged.
  const { isDark, toggleTheme } = useTheme();
  const darkMode = isDark;
  const [showUserActions, setShowUserActions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [error, setError] = useState(null);
  const [banConfirmUserId, setBanConfirmUserId] = useState(null); // <-- Add this line
  // Erase (GDPR Article 17) confirm panel -- separate from banConfirmUserId's
  // simple yes/no because erasure is irreversible: the admin must type the
  // account's username back (checked against the row they're looking at,
  // and again server-side) plus a reason, not just click a second button.
  const [eraseConfirmUserId, setEraseConfirmUserId] = useState(null);
  const [eraseUsernameInput, setEraseUsernameInput] = useState('');
  const [eraseReasonInput, setEraseReasonInput] = useState('');
  const closeEraseConfirm = useCallback(() => {
    setEraseConfirmUserId(null);
    setEraseUsernameInput('');
    setEraseReasonInput('');
  }, []);
  const [viewUserId, setViewUserId] = useState(null);
  // Company Ops access -- null while loading. Full admins get every tab
  // below plus 'companyOps'; non-admin staff (mapped into an OrgRole, see
  // UserOrgRole on the backend) only ever get 'companyOps'. PrivateAdminRoute
  // already decided this user belongs on /admin at all -- this only decides
  // which tabs they see once they're here.
  const [allowedTabs, setAllowedTabs] = useState(null);

  // Redux selectors with proper error handling
  const health = useSelector(state => state.admin?.health);
  const users = useSelector(state => state.admin?.users || []);
  const usersMetadata = useSelector(state => state.admin?.usersMetadata || { total: 0, totalPages: 1 });
  const systemStats = useSelector(state => state.admin?.systemStats);
  const auditLogs = useSelector(state => state.admin?.auditLogs || []);
  const auditLogsMetadata = useSelector(state => state.admin?.auditLogsMetadata || {});
  const loading = useSelector(state => state.admin?.loading || {});
  const errors = useSelector(state => state.admin?.errors || {});
  const bulkActionResult = useSelector(state => state.admin?.bulkActionResult);
  const userDetails = useSelector(state => state.admin?.userDetails);
  const userDetailsLoading = useSelector(state => state.admin?.loading?.userDetails);
  const userDetailsError = useSelector(state => state.admin?.errors?.userDetails);

  const authUser = useSelector(state => state.auth?.user);
  const isFullAdmin = authUser?.is_admin === true || authUser?.role === 'admin';

  // Company Ops access tier -- computed server-side (GET /admin/access) since
  // that's also where staff_required's OrgRole check lives; the frontend
  // just uses it to decide which tabs to render, not as the real security
  // boundary (every backend route re-checks admin_required/staff_required
  // independently). Non-admin staff only ever see 'companyOps', so land them
  // there directly instead of the admin-only 'overview' default.
  useEffect(() => {
    let cancelled = false;
    apiClient.get('/admin/access').then((res) => {
      if (cancelled) return;
      const tabs = res.data?.allowed_tabs || ['companyOps'];
      setAllowedTabs(tabs);
      if (!tabs.includes(activeTab)) setActiveTab(tabs[0]);
    }).catch(() => {
      if (!cancelled) setAllowedTabs(['companyOps']);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize data on component mount with proper error handling. Skipped
  // for non-admin Company Ops staff -- every one of these calls is
  // admin_required-gated on the backend and would just 403 for them.
  useEffect(() => {
    if (!isFullAdmin) return;

    const initializeData = async () => {
      try {
        setError(null);
        const promises = [
          dispatch(fetchAdminHealthThunk()),
          dispatch(fetchAdminSystemStatsThunk()),
          dispatch(fetchAdminUsersListThunk({ page: 1, limit: 50, q: searchTerm || undefined })),
          dispatch(fetchAdminAuditLogsThunk({ page: 1, limit: 50 }))
        ];

        const results = await Promise.allSettled(promises);

        // Check for any rejected promises
        const failedRequests = results.filter(result => result.status === 'rejected');
        if (failedRequests.length > 0) {
          console.warn('Some admin dashboard requests failed:', failedRequests);
          setError(t('admin.dashboard.errors.someDataFailed'));
        }
      } catch (error) {
        console.warn('Failed to initialize admin dashboard:', error);
        setError(t('admin.dashboard.errors.initFailed'));
      }
    };

    initializeData();
  }, [dispatch, t, isFullAdmin]);

  // Debounced search with error handling
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (searchTerm.trim() !== '') {
        try {
          await dispatch(fetchAdminUsersListThunk({ 
            page: 1, 
            limit: pageSize, 
            q: searchTerm.trim() 
          }));
          setCurrentPage(1); // Reset to first page on search
        } catch (error) {
          console.warn('Search failed:', error);
          setError(t('admin.dashboard.errors.searchFailed'));
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dispatch, searchTerm, pageSize, t]);

  // Handle pagination
  useEffect(() => {
    const fetchUsersPage = async () => {
      try {
        await dispatch(fetchAdminUsersListThunk({ 
          page: currentPage, 
          limit: pageSize, 
          q: searchTerm || undefined 
        }));
      } catch (error) {
        console.warn('Failed to fetch users page:', error);
        setError(t('admin.dashboard.errors.fetchUsersFailed'));
      }
    };

    fetchUsersPage();
  }, [dispatch, currentPage, pageSize, searchTerm, t]);

  // Handle bulk user actions with proper error handling and validation
  const handleBulkAction = useCallback(async (action) => {
    if (!selectedUsers.length) {
      setError(t('admin.dashboard.errors.selectUsersFirst'));
      return;
    }

    // Validate action
    const validActions = ['activate', 'ban', 'promote', 'demote', 'suspend'];
    if (!validActions.includes(action)) {
      setError(t('admin.dashboard.errors.invalidAction', { action }));
      return;
    }

    try {
      setError(null);

      const result = await dispatch(bulkAdminUserActionThunk({
        action,
        user_ids: selectedUsers,
        reason: `Bulk ${action} by admin`
      }));

      if (result.type.endsWith('/rejected')) {
        throw new Error(result.error?.message || t('admin.dashboard.errors.bulkActionFailedGeneric', { action }));
      }

      // Refresh users list after successful action
      await dispatch(fetchAdminUsersListThunk({
        page: currentPage,
        limit: pageSize,
        q: searchTerm || undefined
      }));

      setSelectedUsers([]);

      // Show success message
      setError(null);
      // You could add a success toast here instead of alert
      //console.log(`Successfully performed ${action} on ${selectedUsers.length} user(s)`);

    } catch (error) {
      console.warn(`Bulk ${action} failed:`, error);
      setError(t('admin.dashboard.errors.bulkActionFailed', { action, message: error.message }));
    }
  }, [selectedUsers, currentPage, pageSize, searchTerm, dispatch, t]);

  // Export data with proper error handling
  const handleExportData = useCallback(async (entity, format) => {
    if (!['users', 'audit_logs'].includes(entity)) {
      setError(t('admin.dashboard.errors.invalidExportEntity', { entity }));
      return;
    }

    if (!['csv', 'json'].includes(format)) {
      setError(t('admin.dashboard.errors.invalidExportFormat', { format }));
      return;
    }

    try {
      setError(null);

      const result = await dispatch(exportAdminDataThunk({
        entity,
        format,
        filters: entity === 'users' ? { q: searchTerm || undefined } : null
      }));

      if (result.type.endsWith('/rejected')) {
        throw new Error(result.error?.message || t('admin.dashboard.errors.exportFailedGeneric'));
      }

      // Handle blob download
      if (result.payload instanceof Blob) {
        const url = URL.createObjectURL(result.payload);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${entity}_export_${new Date().toISOString().slice(0,10)}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.warn('Export failed:', error);
      setError(t('admin.dashboard.errors.exportFailed', { message: error.message }));
    }
  }, [searchTerm, dispatch, t]);

  // Individual user action with validation
  const handleUserAction = useCallback(async (userId, action) => {
    if (!userId || !action) {
      setError(t('admin.dashboard.errors.invalidUserActionParams'));
      return;
    }

    const validActions = ['suspend', 'activate', 'ban', 'promote', 'demote'];
    if (!validActions.includes(action)) {
      setError(t('admin.dashboard.errors.invalidAction', { action }));
      return;
    }

    try {
      setError(null);

      const result = await dispatch(bulkAdminUserActionThunk({
        action,
        user_ids: [userId],
        reason: `Individual ${action} action by admin`
      }));

      if (result.type.endsWith('/rejected')) {
        throw new Error(result.error?.message || t('admin.dashboard.errors.userActionFailedGeneric', { action }));
      }

      // Refresh users list
      await dispatch(fetchAdminUsersListThunk({
        page: currentPage,
        limit: pageSize,
        q: searchTerm || undefined
      }));

      setShowUserActions({});
      //console.log(`Successfully performed ${action} on user ${userId}`);

    } catch (error) {
      console.warn(`User action ${action} failed:`, error);
      setError(t('admin.dashboard.errors.userActionFailed', { action, message: error.message }));
    }
  }, [currentPage, pageSize, searchTerm, dispatch, t]);

  // Permanently erase a user's account (GDPR Article 17). `user` is the row
  // object the admin clicked, not a bare id -- the typed confirmation is
  // checked against ITS username client-side (instant feedback) and the
  // backend independently re-checks the same thing server-side, so a stale
  // list or wrong-row click fails loudly instead of erasing the wrong person.
  const handleEraseUser = useCallback(async (user) => {
    if (!user?.id) return;

    const typedUsername = eraseUsernameInput.trim();
    if (typedUsername.toLowerCase() !== (user.username || '').trim().toLowerCase()) {
      setError(t('admin.dashboard.errors.eraseUsernameMismatch'));
      return;
    }
    const reason = eraseReasonInput.trim();
    if (reason.length < 10) {
      setError(t('admin.dashboard.errors.eraseReasonTooShort'));
      return;
    }

    try {
      setError(null);

      const result = await dispatch(eraseAdminUserThunk({
        userId: user.id,
        reason,
        confirm_username: typedUsername,
      }));

      if (result.type.endsWith('/rejected')) {
        // result.payload carries the actual backend detail message (e.g. the
        // confirm_username mismatch text) -- see adminApi.js's eraseUser().
        throw new Error(result.payload || t('admin.dashboard.errors.eraseFailedGeneric'));
      }

      // Refresh users list
      await dispatch(fetchAdminUsersListThunk({
        page: currentPage,
        limit: pageSize,
        q: searchTerm || undefined
      }));

      setShowUserActions({});
      closeEraseConfirm();

    } catch (error) {
      console.warn('Erase user failed:', error);
      setError(t('admin.dashboard.errors.eraseFailed', { message: error.message }));
    }
  }, [eraseUsernameInput, eraseReasonInput, currentPage, pageSize, searchTerm, dispatch, t, closeEraseConfirm]);

  // Format uptime with validation
  const formatUptime = (seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return '0d 0h 0m';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  // Format bytes with validation
  const formatBytes = (bytes) => {
    if (typeof bytes !== 'number' || bytes <= 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Theme classes
  const themeClasses = {
    bg: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    // Driven by this component's own `darkMode` state, not Tailwind's
    // `dark:` variant -- those two aren't guaranteed to agree (this
    // dashboard has its own light/dark toggle independent of the global
    // theme context), so a raw `text-green-600 dark:text-green-400` could
    // resolve to green-600 (light-mode contrast ratio) against cardBg's
    // dark background whenever darkMode=true but the global `dark` class
    // wasn't set -- exactly the insufficient-contrast case the pass-28
    // a11y sweep caught here.
    successText: darkMode ? 'text-green-400' : 'text-green-600',
    dangerText: darkMode ? 'text-red-400' : 'text-red-600',
    warningText: darkMode ? 'text-yellow-400' : 'text-yellow-600',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    borderLight: darkMode ? 'border-gray-600' : 'border-gray-300',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    tableBg: darkMode ? 'bg-gray-800' : 'bg-white',
    tableHeaderBg: darkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRowHover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
  };

  // Error display component
  const ErrorDisplay = ({ error, onDismiss }) => (
    <div className={`${darkMode ? 'bg-red-900/50 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-2`} />
          <span className={`${darkMode ? 'text-red-300' : 'text-red-800'} font-medium`}>
            {error}
          </span>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Error Display */}
      {(error || errors.health || errors.systemStats) && (
        <ErrorDisplay 
          error={error || errors.health || errors.systemStats} 
          onDismiss={() => setError(null)}
        />
      )}

      {/* Quick Stats -- skeleton-shaped while loading.health/systemStats is
          true, instead of a spinner banner ABOVE cards that simultaneously
          rendered with "0"/fallback values, which flashed fake zeros before
          the real numbers arrived. */}
      {(loading.health || loading.systemStats) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm animate-pulse`}>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className={`h-3.5 w-24 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-7 w-16 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
                <div className={`h-12 w-12 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
              <div className={`mt-4 h-3.5 w-32 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </div>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.dashboard.overview.totalUsers')}</p>
              <p className={`text-2xl font-bold ${themeClasses.text}`}>
                {usersMetadata.total?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            <span className={`${themeClasses.successText} font-medium`}>
              {users.length > 0 ? t('admin.dashboard.overview.loadedCount', { count: users.length }) : t('admin.dashboard.overview.noData')}
            </span>
            <span className={`${themeClasses.textMuted} ml-1`}>{t('admin.dashboard.overview.inCurrentView')}</span>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.dashboard.overview.activeUsers')}</p>
              <p className={`text-2xl font-bold ${themeClasses.text}`}>
                {users.filter(u => u.is_active).length.toLocaleString()}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className={`${themeClasses.successText} font-medium`}>
              {users.length > 0
                ? `${((users.filter(u => u.is_active).length / users.length) * 100).toFixed(1)}%`
                : '0%'}
            </span>
            <span className={`${themeClasses.textMuted} ml-1`}>{t('admin.dashboard.overview.ofLoadedUsers')}</span>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.dashboard.overview.systemHealth')}</p>
              <p className={`text-2xl font-bold ${
                health?.status === 'healthy' || systemStats?.status === 'healthy'
                  ? themeClasses.successText
                  : health?.status || systemStats?.status
                    ? themeClasses.dangerText
                    : themeClasses.textMuted
              }`}>
                {health?.status || systemStats?.status
                  ? (health?.status || systemStats?.status).charAt(0).toUpperCase() +
                    (health?.status || systemStats?.status).slice(1)
                  : t('admin.dashboard.overview.unknown')
                }
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Clock className={`h-4 w-4 ${themeClasses.textMuted} mr-1`} />
            <span className={themeClasses.textMuted}>
              {t('admin.dashboard.overview.uptimeLabel', { uptime: formatUptime(health?.uptime || systemStats?.uptime || 0) })}
            </span>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.dashboard.overview.cpuUsage')}</p>
              <p className={`text-2xl font-bold ${themeClasses.text}`}>
                {systemStats?.resources?.cpu?.percent?.toFixed(1) || '0.0'}%
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Cpu className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className={`font-medium ${
              !systemStats?.resources?.cpu?.percent
                ? themeClasses.textMuted
                : systemStats.resources.cpu.percent < 70
                  ? themeClasses.successText
                  : themeClasses.warningText
            }`}>
              {!systemStats?.resources?.cpu?.percent ? t('admin.dashboard.overview.noData') : systemStats.resources.cpu.percent < 70 ? t('admin.dashboard.overview.normal') : t('admin.dashboard.overview.high')}
            </span>
            <span className={`${themeClasses.textMuted} ml-1`}>{t('admin.dashboard.overview.usageSuffix')}</span>
          </div>
        </div>
      </div>
      )}

      {/* System Resources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>{t('admin.dashboard.overview.systemResources')}</h3>
          {(loading.health || loading.systemStats) ? (
            <div className="space-y-4 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <div className="flex justify-between items-center mb-2">
                    <div className={`h-3.5 w-28 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <div className={`h-3.5 w-10 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  </div>
                  <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : systemStats?.resources ? (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${themeClasses.textSecondary} flex items-center`}>
                    <Cpu className="h-4 w-4 mr-2" />
                    {t('admin.dashboard.overview.cpuUsage')}
                  </span>
                  <span className={`text-sm font-bold ${themeClasses.text}`}>
                    {systemStats.resources.cpu?.percent?.toFixed(1) || '0.0'}%
                  </span>
                </div>
                <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(systemStats.resources.cpu?.percent || 0, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${themeClasses.textSecondary} flex items-center`}>
                    <HardDrive className="h-4 w-4 mr-2" />
                    {t('admin.dashboard.overview.memoryUsage')}
                  </span>
                  <span className={`text-sm font-bold ${themeClasses.text}`}>
                    {systemStats.resources.memory?.percent_used?.toFixed(1) || '0.0'}%
                  </span>
                </div>
                <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(systemStats.resources.memory?.percent_used || 0, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${themeClasses.textSecondary} flex items-center`}>
                    <Database className="h-4 w-4 mr-2" />
                    {t('admin.dashboard.overview.diskUsage')}
                  </span>
                  <span className={`text-sm font-bold ${themeClasses.text}`}>
                    {systemStats.resources.disk?.percent_used?.toFixed(1) || '0.0'}%
                  </span>
                </div>
                <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div 
                    className="bg-yellow-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(systemStats.resources.disk?.percent_used || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Server className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.overview.resourceDataUnavailable')}</p>
            </div>
          )}
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>{t('admin.dashboard.overview.recentActivity')}</h3>
          {(loading.health || loading.systemStats) ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
                  <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                  <div className="flex-1 space-y-1.5">
                    <div className={`h-3.5 w-1/2 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                    <div className={`h-3 w-1/3 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                  </div>
                </div>
              ))}
            </div>
          ) : auditLogs.length > 0 ? (
            <div className="space-y-3">
              {auditLogs.slice(0, 5).map((log) => (
                <div key={log.id} className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
                  <div className={`w-2 h-2 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                      {log.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('admin.dashboard.overview.unknownAction')}
                    </p>
                    <p className={`text-xs ${themeClasses.textMuted}`}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : t('admin.dashboard.overview.unknownTime')}
                    </p>
                  </div>
                  <div className={`text-xs ${themeClasses.textMuted}`}>{log.ip_address || t('admin.dashboard.overview.unknownIp')}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.overview.noRecentActivity')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const UsersTab = () => {
    const filteredUsers = users.filter(user => {
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        user.email?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower) ||
        (user.profile?.full_name || '').toLowerCase().includes(searchLower)
      );
    });

    return (
      <div className="space-y-6">
        {/* Error Display */}
        {error && <ErrorDisplay error={error} onDismiss={() => setError(null)} />}

        {/* Search and Controls */}
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textMuted} h-5 w-5`} />
              <input
                type="text"
                placeholder={t('admin.dashboard.users.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              />
            </div>
            <div className="flex gap-3">
              <button 
                disabled={true}
                className={`flex items-center px-4 py-2 border ${themeClasses.borderLight} ${themeClasses.buttonSecondary} rounded-lg transition-colors opacity-50 cursor-not-allowed`}
                title={t('admin.dashboard.users.filterTitle')}
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('admin.dashboard.users.filter')}
              </button>
              <button
                onClick={() => handleExportData('users', 'csv')}
                disabled={loading.exportData}
                className={`flex items-center px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors disabled:opacity-50`}
              >
                <Download className="h-4 w-4 mr-2" />
                {t('admin.dashboard.users.export')}
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <div className={`mt-4 p-4 ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} rounded-lg border`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  {t('admin.dashboard.users.selectedCount', { count: selectedUsers.length })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkAction('activate')}
                    disabled={loading.bulkAction}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <UserCheck className="h-3 w-3 mr-1 inline" />
                    {t('admin.dashboard.users.activate')}
                  </button>
                  <button
                    onClick={() => handleBulkAction('ban')}
                    disabled={loading.bulkAction}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <UserX className="h-3 w-3 mr-1 inline" />
                    {t('admin.dashboard.users.ban')}
                  </button>
                  <button
                    onClick={() => handleBulkAction('promote')}
                    disabled={loading.bulkAction}
                    className="px-3 py-1 text-xs bg-violet-700 text-white rounded hover:bg-violet-600 disabled:opacity-50 transition-colors"
                  >
                    <Crown className="h-3 w-3 mr-1 inline" />
                    {t('admin.dashboard.users.promote')}
                  </button>
                  <button
                    onClick={() => handleBulkAction('demote')}
                    disabled={loading.bulkAction}
                    className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    <UserX className="h-3 w-3 mr-1 inline" />
                    {t('admin.dashboard.users.demote')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className={`${themeClasses.tableBg} rounded-xl border ${themeClasses.border} shadow-sm overflow-hidden`}>
          {loading.users ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={themeClasses.tableHeaderBg}>
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <div className={`h-4 w-4 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableUser')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableStatus')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableRole')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableLastLogin')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableActions')}</th>
                  </tr>
                </thead>
                <tbody className={`${themeClasses.tableBg} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className={`h-4 w-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className="ml-4 space-y-2">
                            <div className={`h-3.5 w-28 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                            <div className={`h-3 w-36 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4"><div className={`h-5 w-16 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                      <td className="px-6 py-4"><div className={`h-5 w-14 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                      <td className="px-6 py-4"><div className={`h-3.5 w-20 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={themeClasses.tableHeaderBg}>
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(filteredUsers.map(u => u.id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                      />
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableUser')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableStatus')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableRole')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableLastLogin')}</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.users.tableActions')}</th>
                  </tr>
                </thead>
                <tbody className={`${themeClasses.tableBg} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={themeClasses.tableRowHover}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.profile?.full_name?.split(' ').map(n => n[0]).join('') || user.username?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          {/* min-w-0 lets the truncate below actually work inside this
                              flex row -- without it the email ran to the raw edge of
                              the viewport with a hard cutoff and no ellipsis instead
                              of truncating cleanly (found live during the 2026-07-17
                              mobile audit). max-w keeps it readable on wide screens too. */}
                          <div className="ml-4 min-w-0 max-w-[180px] sm:max-w-xs">
                            <div className={`text-sm font-medium truncate ${themeClasses.text}`} title={user.username || t('admin.dashboard.users.unknown')}>{user.username || t('admin.dashboard.users.unknown')}</div>
                            <div className={`text-sm truncate ${themeClasses.textMuted}`} title={user.email || t('admin.dashboard.users.noEmail')}>{user.email || t('admin.dashboard.users.noEmail')}</div>
                            {user.profile?.full_name && (
                              <div className={`text-xs truncate ${themeClasses.textMuted}`} title={user.profile.full_name}>{user.profile.full_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                          {user.is_active ? t('admin.dashboard.users.active') : t('admin.dashboard.users.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_admin
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {user.is_admin ? t('admin.dashboard.users.admin') : t('admin.dashboard.users.user')}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-sm ${themeClasses.textMuted}`}>
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : t('admin.dashboard.users.never')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setViewUserId(user.id);
                              dispatch(fetchAdminUserThunk(user.id));
                            }}
                            className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-900'} transition-colors`}
                            title={t('admin.dashboard.users.viewDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <div className="relative">
                            <button 
                              onClick={() => setShowUserActions(prev => ({
                                ...prev,
                                [user.id]: !prev[user.id]
                              }))}
                              className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                            {showUserActions[user.id] && (
                              <div className={`absolute right-0 mt-2 w-48 ${themeClasses.cardBg} border ${themeClasses.border} rounded-md shadow-lg z-10`}>
                                <div className="py-1">
                                  <button
                                    onClick={() => handleUserAction(user.id, user.is_active ? 'suspend' : 'activate')}
                                    className={`block w-full text-left px-4 py-2 text-sm ${themeClasses.text} ${themeClasses.hoverBg}`}
                                  >
                                    {user.is_active ? t('admin.dashboard.users.suspendUser') : t('admin.dashboard.users.activateUser')}
                                  </button>
                                  <button
                                    onClick={() => handleUserAction(user.id, user.is_admin ? 'demote' : 'promote')}
                                    className={`block w-full text-left px-4 py-2 text-sm ${themeClasses.text} ${themeClasses.hoverBg}`}
                                  >
                                    {user.is_admin ? t('admin.dashboard.users.removeAdmin') : t('admin.dashboard.users.makeAdmin')}
                                  </button>
                                  <button
                                    onClick={() => setBanConfirmUserId(user.id)}
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    {t('admin.dashboard.users.banUser')}
                                  </button>
                                  {banConfirmUserId === user.id && (
                                    <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                                      <p className="text-xs text-red-600 dark:text-red-400 mb-2 font-medium">{t('admin.dashboard.users.banConfirmQuestion')}</p>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => {
                                            handleUserAction(user.id, 'ban');
                                            setBanConfirmUserId(null);
                                          }}
                                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                        >
                                          {t('admin.dashboard.users.confirm')}
                                        </button>
                                        <button
                                          onClick={() => setBanConfirmUserId(null)}
                                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        >
                                          {t('admin.dashboard.users.cancel')}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {user.id !== authUser?.id && (
                                    <button
                                      onClick={() => {
                                        setEraseConfirmUserId(user.id);
                                        setEraseUsernameInput('');
                                        setEraseReasonInput('');
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 border-t border-gray-200 dark:border-gray-700"
                                    >
                                      <Trash2 className="h-3 w-3 mr-1.5 inline" />
                                      {t('admin.dashboard.users.eraseUser')}
                                    </button>
                                  )}
                                  {eraseConfirmUserId === user.id && (
                                    <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800 space-y-2">
                                      <p className="text-xs text-red-700 dark:text-red-400 font-medium">
                                        {t('admin.dashboard.users.eraseWarning', { username: user.username, email: user.email })}
                                      </p>
                                      <div>
                                        <label className="block text-[11px] text-red-600 dark:text-red-400 mb-1">
                                          {t('admin.dashboard.users.eraseTypeUsername', { username: user.username })}
                                        </label>
                                        <input
                                          type="text"
                                          value={eraseUsernameInput}
                                          onChange={(e) => setEraseUsernameInput(e.target.value)}
                                          placeholder={user.username}
                                          autoComplete="off"
                                          className="w-full px-2 py-1 text-xs border border-red-300 dark:border-red-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[11px] text-red-600 dark:text-red-400 mb-1">
                                          {t('admin.dashboard.users.eraseReasonLabel')}
                                        </label>
                                        <textarea
                                          value={eraseReasonInput}
                                          onChange={(e) => setEraseReasonInput(e.target.value)}
                                          placeholder={t('admin.dashboard.users.eraseReasonPlaceholder')}
                                          rows={2}
                                          className="w-full px-2 py-1 text-xs border border-red-300 dark:border-red-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                                        />
                                      </div>
                                      <div className="flex gap-2 pt-1">
                                        <button
                                          onClick={() => handleEraseUser(user)}
                                          disabled={
                                            loading.eraseUser ||
                                            eraseUsernameInput.trim().toLowerCase() !== (user.username || '').trim().toLowerCase() ||
                                            eraseReasonInput.trim().length < 10
                                          }
                                          className="px-2 py-1 text-xs bg-red-700 text-white rounded hover:bg-red-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          {loading.eraseUser ? t('admin.dashboard.users.erasing') : t('admin.dashboard.users.eraseConfirmButton')}
                                        </button>
                                        <button
                                          onClick={closeEraseConfirm}
                                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        >
                                          {t('admin.dashboard.users.cancel')}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {!loading.users && filteredUsers.length === 0 && (
            <div className={`text-center py-12 ${themeClasses.textMuted}`}>
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {searchTerm ? t('admin.dashboard.users.noUsersFoundSearch') : t('admin.dashboard.users.noUsersAvailable')}
              </p>
              {searchTerm && (
                <p className="text-sm">{t('admin.dashboard.users.tryAdjustingSearch')}</p>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {usersMetadata.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className={`text-sm ${themeClasses.textMuted}`}>
              {t('admin.dashboard.users.showingUsers', { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, usersMetadata.total), total: usersMetadata.total })}
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 text-sm border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded`}
              >
                <option value={25}>{t('admin.dashboard.users.perPage', { count: 25 })}</option>
                <option value={50}>{t('admin.dashboard.users.perPage', { count: 50 })}</option>
                <option value={100}>{t('admin.dashboard.users.perPage', { count: 100 })}</option>
              </select>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
              >
                {t('admin.dashboard.users.previous')}
              </button>
              <span className={`px-3 py-2 text-sm ${themeClasses.text}`}>
                {t('admin.dashboard.users.pageOf', { page: currentPage, totalPages: usersMetadata.totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, usersMetadata.totalPages))}
                disabled={currentPage === usersMetadata.totalPages}
                className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
              >
                {t('admin.dashboard.users.next')}
              </button>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {viewUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 w-full max-w-md mx-4 shadow-xl`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.dashboard.users.detailsModal.title')}</h3>
                <button
                  onClick={() => {
                    setViewUserId(null);
                    dispatch(clearUserDetails());
                  }}
                  className={`${themeClasses.textMuted} hover:${themeClasses.text}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {userDetailsLoading && (
                <p className={`text-sm ${themeClasses.textMuted}`}>{t('admin.dashboard.users.detailsModal.loading')}</p>
              )}
              {!userDetailsLoading && userDetailsError && (
                <p className="text-sm text-red-500">{userDetailsError}</p>
              )}
              {!userDetailsLoading && !userDetailsError && userDetails && userDetails.id === viewUserId && (
                <dl className="space-y-3 text-sm">
                  {[
                    [t('admin.dashboard.users.detailsModal.id'), userDetails.id],
                    [t('admin.dashboard.users.detailsModal.username'), userDetails.username],
                    [t('admin.dashboard.users.detailsModal.email'), userDetails.email],
                    [t('admin.dashboard.users.detailsModal.fullName'), userDetails.profile?.full_name || '—'],
                    [t('admin.dashboard.users.detailsModal.status'), userDetails.is_active ? t('admin.dashboard.users.active') : t('admin.dashboard.users.inactive')],
                    [t('admin.dashboard.users.detailsModal.role'), userDetails.is_admin ? t('admin.dashboard.users.admin') : t('admin.dashboard.users.user')],
                    [t('admin.dashboard.users.detailsModal.created'), userDetails.created_at ? new Date(userDetails.created_at).toLocaleString() : '—'],
                    [t('admin.dashboard.users.detailsModal.lastLogin'), userDetails.last_login ? new Date(userDetails.last_login).toLocaleString() : t('admin.dashboard.users.never')],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4">
                      <dt className={themeClasses.textMuted}>{label}</dt>
                      <dd className={`${themeClasses.text} text-right break-all`}>{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {!userDetailsLoading && !userDetailsError && userDetails && userDetails.id === viewUserId && (
                <UserAssignedTasksSection userId={userDetails.id} themeClasses={themeClasses} t={t} />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const SystemTab = () => (
    <div className="space-y-6">
      {/* Error Display */}
      {error && <ErrorDisplay error={error} onDismiss={() => setError(null)} />}

      {/* System Info Header -- title block and status+refresh block had no
          flex-wrap, so at narrow widths the "System Status" title wrapped
          and visually collided with "Environment: ..." below it while the
          status pill got squeezed against the title column (found live
          during the 2026-07-17 mobile audit). Stacks cleanly below sm now,
          matching the flex-col/sm:flex-row header pattern already
          established elsewhere in this app (e.g. GroupListPage.jsx). */}
      <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className={`text-xl font-semibold ${themeClasses.text}`}>{t('admin.dashboard.system.title')}</h2>
            <p className={themeClasses.textSecondary}>{t('admin.dashboard.system.environment', { env: systemStats?.environment || t('admin.dashboard.overview.unknown') })}</p>
          </div>
          <div className="flex items-center flex-wrap gap-3">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${
                health?.status === 'healthy' || systemStats?.status === 'healthy' 
                  ? 'bg-green-500' 
                  : health?.status || systemStats?.status
                    ? 'bg-red-500'
                    : 'bg-gray-500'
              }`}></div>
              <span className={`text-sm font-medium ${
                health?.status === 'healthy' || systemStats?.status === 'healthy' 
                  ? 'text-green-700 dark:text-green-400' 
                  : health?.status || systemStats?.status
                    ? 'text-red-700 dark:text-red-400'
                    : themeClasses.textMuted
              }`}>
                {health?.status === 'healthy' || systemStats?.status === 'healthy'
                  ? t('admin.dashboard.system.allOperational')
                  : health?.status || systemStats?.status
                    ? t('admin.dashboard.system.issuesDetected')
                    : t('admin.dashboard.system.statusUnknown')}
              </span>
            </div>
            <button
              onClick={async () => {
                try {
                  setError(null);
                  await Promise.all([
                    dispatch(fetchAdminHealthThunk()),
                    dispatch(fetchAdminSystemStatsThunk())
                  ]);
                } catch (err) {
                  setError(t('admin.dashboard.errors.refreshSystemFailed'));
                }
              }}
              disabled={loading.systemStats || loading.health}
              className={`flex items-center px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors disabled:opacity-50`}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${(loading.systemStats || loading.health) ? 'animate-spin' : ''}`} />
              {t('admin.dashboard.system.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Detailed System Metrics -- each card skeletons its own body while
          loading instead of a spinner banner above both (which used to
          render simultaneously with their "unavailable" fallback text). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4 flex items-center`}>
            <Server className="h-5 w-5 mr-2" />
            {t('admin.dashboard.system.serverInformation')}
          </h3>
          {(loading.systemStats || loading.health) ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex justify-between">
                  <div className={`h-3.5 w-24 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3.5 w-28 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : systemStats?.system ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.hostname')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.system.hostname || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.platform')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.system.platform || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.pythonVersion')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.system.python_version || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.uptime')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{formatUptime(systemStats.system.uptime_seconds || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.appVersion')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.version || t('admin.dashboard.system.notAvailable')}</span>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Server className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.system.serverInfoUnavailable')}</p>
            </div>
          )}
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4 flex items-center`}>
            <Wifi className="h-5 w-5 mr-2" />
            {t('admin.dashboard.system.databaseCache')}
          </h3>
          {(loading.systemStats || loading.health) ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex justify-between">
                  <div className={`h-3.5 w-28 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3.5 w-20 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : systemStats?.cache ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.redisVersion')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.cache.redis_version || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.redisMemory')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.cache.used_memory_human || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.connectedClients')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.cache.connected_clients || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.cacheUptime')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{t('admin.dashboard.system.daysSuffix', { count: systemStats.cache.uptime_in_days || 0 })}</span>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.system.dbCacheUnavailable')}</p>
            </div>
          )}
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4 flex items-center`}>
            <Cpu className="h-5 w-5 mr-2" />
            {t('admin.dashboard.system.processInformation')}
          </h3>
          {systemStats?.process ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.processId')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.process.pid || t('admin.dashboard.system.notAvailable')}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.memoryUsageLabel')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.process.memory_mb?.toFixed(2) || '0'} MB</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.cpuUsageLabel')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.process.cpu_percent || '0'}%</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.threads')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.process.threads || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.connections')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.process.connections || 0}</span>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.system.processInfoUnavailable')}</p>
            </div>
          )}
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4 flex items-center`}>
            <Wifi className="h-5 w-5 mr-2" />
            {t('admin.dashboard.system.networkStats')}
          </h3>
          {systemStats?.resources?.network ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.bytesSent')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{formatBytes(systemStats.resources.network.bytes_sent || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.bytesReceived')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{formatBytes(systemStats.resources.network.bytes_recv || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.packetsSent')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.resources.network.packets_sent?.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t('admin.dashboard.system.packetsReceived')}</span>
                <span className={`font-medium ${themeClasses.text}`}>{systemStats.resources.network.packets_recv?.toLocaleString() || '0'}</span>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${themeClasses.textMuted}`}>
              <Wifi className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('admin.dashboard.system.networkStatsUnavailable')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Alerts Section */}
      {systemStats?.alerts?.length > 0 && (
        <div className={`${darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-orange-600'} rounded-xl border p-6 shadow-sm`}>
          <div className="flex items-center mb-2">
            <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'} mr-2`} />
            <span className={`${darkMode ? 'text-yellow-300' : 'text-yellow-800'} font-medium`}>{t('admin.dashboard.system.systemAlerts')}</span>
          </div>
          <ul className="list-disc ml-6">
            {systemStats.alerts.map((alert, idx) => (
              <li key={idx} className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-900'}`}>{alert}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const AuditLogsTab = () => (
    <div className="space-y-6">
      {/* Error Display */}
      {error && <ErrorDisplay error={error} onDismiss={() => setError(null)} />}

      <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${themeClasses.text} flex items-center`}>
            <FileText className="h-5 w-5 mr-2" />
            {t('admin.dashboard.auditLogs.title')}
          </h3>
          <button
            onClick={() => handleExportData('audit_logs', 'csv')}
            disabled={loading.exportData}
            className={`flex items-center px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors disabled:opacity-50`}
          >
            <Download className="h-4 w-4 mr-2" />
            {t('admin.dashboard.auditLogs.exportLogs')}
          </button>
        </div>

        {loading.auditLogs ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={themeClasses.tableHeaderBg}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.timestamp')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.userId')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.action')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.ipAddress')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.status')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.details')}</th>
                </tr>
              </thead>
              <tbody className={`${themeClasses.tableBg} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className={`h-3.5 w-32 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                    <td className="px-6 py-4"><div className={`h-3.5 w-10 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                    <td className="px-6 py-4"><div className={`h-3.5 w-28 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                    <td className="px-6 py-4"><div className={`h-3.5 w-20 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                    <td className="px-6 py-4"><div className={`h-4 w-4 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                    <td className="px-6 py-4"><div className={`h-3.5 w-40 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : auditLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={themeClasses.tableHeaderBg}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.timestamp')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.userId')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.action')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.ipAddress')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.status')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.dashboard.auditLogs.details')}</th>
                </tr>
              </thead>
              <tbody className={`${themeClasses.tableBg} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {auditLogs.map((log) => (
                  <tr key={log.id} className={themeClasses.tableRowHover}>
                    <td className={`px-6 py-4 text-sm ${themeClasses.text}`}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : t('admin.dashboard.auditLogs.unknown')}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themeClasses.text}`}>
                      {log.user_id || t('admin.dashboard.auditLogs.notAvailable')}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themeClasses.text}`}>
                      {log.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('admin.dashboard.auditLogs.unknownAction')}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themeClasses.textMuted}`}>
                      {log.ip_address || t('admin.dashboard.auditLogs.unknown')}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {log.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      )}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themeClasses.textMuted} max-w-xs truncate`} title={log.details}>
                      {log.details || t('admin.dashboard.auditLogs.noDetails')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`text-center py-12 ${themeClasses.textMuted}`}>
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">{t('admin.dashboard.auditLogs.noLogsAvailable')}</p>
            <p className="text-sm">{t('admin.dashboard.auditLogs.logsWillAppear')}</p>
          </div>
        )}
      </div>
    </div>
  );

  const ImportTab = () => (
    <div className="space-y-6">
      {/* Error Display */}
      {error && <ErrorDisplay error={error} onDismiss={() => setError(null)} />}
      
      <CSVImportSystem />
    </div>
  );

  // Main render - tabs and content. Filtered by `allowedTabs` (from GET
  // /admin/access) once it resolves -- full admins get every tab here plus
  // companyOps; non-admin Company Ops staff only ever get companyOps. While
  // allowedTabs is still loading (null), render nothing but companyOps so a
  // staff-only user never briefly flashes admin-only tabs.
  const allTabs = [
    { key: 'overview', label: t('admin.dashboard.tabs.overview'), icon: <Shield className="h-4 w-4 mr-2" /> },
    { key: 'users', label: t('admin.dashboard.tabs.users'), icon: <Users className="h-4 w-4 mr-2" /> },
    { key: 'system', label: t('admin.dashboard.tabs.system'), icon: <Server className="h-4 w-4 mr-2" /> },
    { key: 'audit', label: t('admin.dashboard.tabs.audit'), icon: <FileText className="h-4 w-4 mr-2" /> },
    { key: 'import', label: t('admin.dashboard.tabs.import'), icon: <Upload className="h-4 w-4 mr-2" /> },
    { key: 'moderation', label: t('admin.dashboard.tabs.moderation'), icon: <Shield className="h-4 w-4 mr-2" /> },
    { key: 'evidenceModeration', label: t('admin.dashboard.tabs.evidenceModeration'), icon: <FileText className="h-4 w-4 mr-2" /> },
    { key: 'newsModeration', label: t('admin.dashboard.tabs.newsModeration'), icon: <Newspaper className="h-4 w-4 mr-2" /> },
    { key: 'platformStatus', label: t('admin.dashboard.tabs.platformStatus'), icon: <Settings className="h-4 w-4 mr-2" /> },
    { key: 'credentialQuiz', label: t('admin.dashboard.tabs.credentialQuiz'), icon: <GraduationCap className="h-4 w-4 mr-2" /> },
    { key: 'statusMap', label: t('admin.dashboard.tabs.statusMap'), icon: <Map className="h-4 w-4 mr-2" /> },
    { key: 'reviewRequests', label: t('admin.dashboard.tabs.reviewRequests'), icon: <ShieldQuestion className="h-4 w-4 mr-2" /> },
    { key: 'meetingNotes', label: t('admin.dashboard.tabs.meetingNotes'), icon: <StickyNote className="h-4 w-4 mr-2" /> },
    { key: 'teamMeetings', label: t('admin.dashboard.tabs.teamMeetings'), icon: <Mic className="h-4 w-4 mr-2" /> },
    { key: 'companyOps', label: t('admin.dashboard.tabs.companyOps'), icon: <Briefcase className="h-4 w-4 mr-2" /> },
  ];
  const tabs = allTabs.filter((tab) => (allowedTabs ? allowedTabs.includes(tab.key) : tab.key === 'companyOps'));

  return (
    <div className={`min-h-0 w-full ${themeClasses.bg} transition-colors duration-200`}>
      <div className="p-6">
        {/* Header with Theme Toggle */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${themeClasses.text}`}>{t('admin.dashboard.header.title')}</h1>
            <p className={`${themeClasses.textMuted} mt-1`}>{t('admin.dashboard.header.subtitle')}</p>
          </div>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg ${themeClasses.buttonSecondary} transition-colors`}
            title={darkMode ? t('admin.dashboard.header.switchToLight') : t('admin.dashboard.header.switchToDark')}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>

        {/* Global Error Display */}
        {(errors.global || error) && (
          <ErrorDisplay 
            error={errors.global || error} 
            onDismiss={() => {
              setError(null);
              // You might want to clear global errors from Redux too
            }} 
          />
        )}

        {/* Tabs Navigation -- 8 tabs with no overflow-x-auto and no
            flex-shrink-0/whitespace-nowrap on the buttons meant flexbox
            compressed whichever tabs didn't fit rather than scrolling them,
            wrapping labels like "Audit Logs"/"Platform Status" onto 2 lines
            while single-word tabs stayed fine (found live during the
            2026-07-17 mobile audit). Explicit horizontal scroll + no-shrink
            buttons instead. */}
        <div className={`flex space-x-1 mb-8 border-b overflow-x-auto ${themeClasses.border}`}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`flex items-center flex-shrink-0 whitespace-nowrap px-6 py-3 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab.key
                  ? `border-blue-600 ${darkMode ? 'text-blue-400 bg-gray-800' : 'text-blue-600 bg-white'}`
                  : `border-transparent ${themeClasses.textMuted} ${themeClasses.hoverBg}`
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'system' && <SystemTab />}
          {activeTab === 'audit' && <AuditLogsTab />}
          {activeTab === 'import' && <ImportTab />}
          {activeTab === 'moderation' && <AdminModerationComponent darkMode={darkMode} />}
          {activeTab === 'evidenceModeration' && <AdminEvidenceModerationComponent darkMode={darkMode} />}
          {activeTab === 'newsModeration' && <AdminNewsModerationComponent darkMode={darkMode} />}
          {activeTab === 'platformStatus' && <AdminPlatformStatusComponent darkMode={darkMode} />}
          {activeTab === 'credentialQuiz' && <AdminCredentialQuizComponent darkMode={darkMode} />}
          {activeTab === 'statusMap' && <AdminStatusMapComponent darkMode={darkMode} />}
          {activeTab === 'reviewRequests' && <AdminReviewRequestsComponent darkMode={darkMode} />}
          {activeTab === 'meetingNotes' && <AdminMeetingNotesComponent darkMode={darkMode} />}
          {activeTab === 'teamMeetings' && <AdminTeamMeetingsComponent darkMode={darkMode} />}
          {activeTab === 'companyOps' && (
            <AdminCompanyOpsComponent
              darkMode={darkMode}
              initialSubView={deepLinkSub}
              initialTaskId={deepLinkTaskId ? Number(deepLinkTaskId) : null}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;